import PropTypes from 'prop-types';
import React from 'react';
import Flatpickr from 'react-flatpickr';
import { Alert, Badge, Button, Plus, Select, TextInput, XClose } from '@opendate-app/setlist';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import PriceBreakoutAccordion from './PriceBreakoutAccordion';
import { parseDateTime, visiblePricePoints } from './ticketTypeFormHelpers';
import { pricePointsError, quantityPricePoint } from '../utils/ticketPricePoints';

import './TicketTypeDrawer.css';

const TRIGGER_OPTIONS = [
  { value: 'date', label: 'Date/time' },
  { value: 'quantity', label: 'Tickets sold' },
];

const TicketPricePoints = ({ ticketType, onChange, onRecalculate, onAdd, onRemove, minDate, maxDate, defaultTrigger = 'date' }) => {
  const pricePoints = visiblePricePoints(ticketType.price_points);
  const quantityDefault = defaultTrigger === 'quantity';
  const hasDatePoints = pricePoints.some((pricePoint) => !quantityPricePoint(pricePoint));
  const validationError = pricePointsError(ticketType.price_points, ticketType);

  const updatePricePoint = (pricePoint, attributes, recalculate = false) => {
    const updated = {
      ...ticketType,
      price_points: ticketType.price_points.map((point) => point.id === pricePoint.id ? { ...point, ...attributes } : point),
    };
    onChange(updated);
    if (recalculate) onRecalculate(updated);
  };

  return (
    <>
      {validationError ? <Alert color="error" description={validationError} /> : null}
      {pricePoints.map((pricePoint, index) => (
        <div key={pricePoint.id} className="ticket-type-drawer-price-point">
          <div className="ticket-type-drawer-price-point-header">
            <Badge color="brand" size="sm">Price Change {index + 1}</Badge>
            <Button variant="tertiary" size="sm" icon={XClose}
              aria-label={`Remove price change ${index + 1}`}
              onClick={() => onRemove(pricePoint)} />
          </div>
          <Select id={`price-point-${pricePoint.id}-trigger`} label="Change price based on"
              options={TRIGGER_OPTIONS.map((option) => ({ ...option, disabled: option.value === 'quantity' && ticketType.dynamic_pricing_enabled }))}
              hint={ticketType.dynamic_pricing_enabled ? 'Turn off dynamic pricing to use tickets-sold price changes.' : undefined}
              value={quantityPricePoint(pricePoint) ? 'quantity' : 'date'}
              onChange={(trigger) => {
                if (trigger === (quantityPricePoint(pricePoint) ? 'quantity' : 'date')) return;
                updatePricePoint(pricePoint, {
                  sold_count_threshold: trigger === 'quantity' ? '' : null,
                  starts_at: trigger === 'quantity' ? null : '',
                });
              }} />
          <div className="ticket-type-drawer-price-point-fields">
            <div className="ticket-drawer-field">
              <TextInput id={`price-point-${pricePoint.id}-name`} label="Description"
                value={pricePoint.name || ''} placeholder="Advanced, Day of Show, etc..."
                hint={`Displays as: ${ticketType.base_name}${pricePoint.name ? ` - ${pricePoint.name}` : ''}`}
                onChange={(event) => updatePricePoint(pricePoint, { name: event.target.value })} />
            </div>
            <div className="ticket-drawer-field">
              {quantityPricePoint(pricePoint) ? (
                <TextInput id={`price-point-${pricePoint.id}-sold-count-threshold`}
                  label="Tickets sold" required type="number" min="1" max="2147483647" step="1"
                  hint="Applies after this many tickets of this type have sold."
                  value={pricePoint.sold_count_threshold}
                  onChange={(event) => updatePricePoint(pricePoint, { sold_count_threshold: event.target.value, starts_at: null })} />
              ) : (
                <div>
                  <label htmlFor={`price-point-${pricePoint.id}-start-date`} className="ticket-drawer-field-label">
                    Date<span className="ticket-drawer-required">&nbsp;*</span>
                  </label>
                  <Flatpickr id={`price-point-${pricePoint.id}-start-date`}
                    options={{ dateFormat: 'M j, Y, h:i K', enableTime: true,
                      minDate: parseDateTime(minDate || null), maxDate: parseDateTime(maxDate || null) }}
                    placeholder="Select date" className="form-control ticketing-date-picker form-control-flatpickr"
                    value={parseDateTime(pricePoint.starts_at)}
                    onClose={(dates) => updatePricePoint(pricePoint, { starts_at: parseDateTime(dates[0]), sold_count_threshold: null })} />
                </div>
              )}
            </div>
            <div className="ticket-drawer-field ticket-type-drawer-price-point-price">
              <TextInput as={CurrencyInput} id={`price-point-${pricePoint.id}-price`} label="Price" required
                leadingText="$" maskOptions={{ prefix: '' }} value={pricePoint.price_two_decimals || ''} placeholder="0.00"
                onChange={(event) => updatePricePoint(pricePoint, { price_two_decimals: event.target.value }, true)} />
            </div>
          </div>
          <PriceBreakoutAccordion item={pricePoint} accordionId={`PricePoint${pricePoint.id}`}
            dynamicPricingInfo={(() => {
              if (!ticketType.dynamic_pricing_enabled || !pricePoint.price_two_decimals) return null;

              const basePrice = parseFloat(ticketType.base_price_two_decimals) || 0;
              const minPrice = parseFloat(ticketType.dynamic_pricing_min) || 0;
              const maxPrice = parseFloat(ticketType.dynamic_pricing_max) || 0;
              const pointPrice = parseFloat(pricePoint.price_two_decimals) || 0;
              const cashFinalCost = parseFloat(pricePoint.cash_final_cost) || 0;
              const cardFinalCost = parseFloat(pricePoint.final_cost) || 0;
              const cashTaxes = parseFloat(pricePoint.cash_taxes) || 0;
              const cardTaxes = parseFloat(pricePoint.taxes) || 0;

              if (basePrice > 0 && pointPrice > 0 && (cashFinalCost > 0 || cardFinalCost > 0)) {
                const minPercentage = ((minPrice - basePrice) / basePrice * 100);
                const maxPercentage = ((maxPrice - basePrice) / basePrice * 100);
                const cashTotal = cashFinalCost + cashTaxes;
                const cardTotal = cardFinalCost + cardTaxes;
                let cashMin = Math.max(0, cashTotal + (cashTotal * minPercentage / 100));
                let cashMax = cashTotal + (cashTotal * maxPercentage / 100);
                let cardMin = Math.max(0, cardTotal + (cardTotal * minPercentage / 100));
                let cardMax = cardTotal + (cardTotal * maxPercentage / 100);

                if (ticketType.round_up) {
                  cashMin = Math.ceil(cashMin);
                  cashMax = Math.ceil(cashMax);
                  cardMin = Math.ceil(cardMin);
                  cardMax = Math.ceil(cardMax);
                }

                return { cashMin, cashMax, cardMin, cardMax };
              }
              return null;
            })()} />
        </div>
      ))}
      {quantityDefault && hasDatePoints ? (
        <p className="ticket-drawer-muted-text">Switch or remove date/time price changes before adding a tickets-sold price change.</p>
      ) : null}
      {quantityDefault && ticketType.dynamic_pricing_enabled ? (
        <p className="ticket-drawer-muted-text">Turn off dynamic pricing to use tickets-sold price changes.</p>
      ) : null}
      <div>
        <Button variant="link-color" icon={Plus} disabled={quantityDefault && (hasDatePoints || ticketType.dynamic_pricing_enabled)}
          onClick={() => onAdd(quantityDefault ? 'quantity' : undefined)}>
          {quantityDefault ? 'Add a tickets-sold price change' : 'Add a price change'}
        </Button>
      </div>
    </>
  );
};

TicketPricePoints.propTypes = {
  ticketType: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onRecalculate: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  defaultTrigger: PropTypes.oneOf(['date', 'quantity']),
};

export default TicketPricePoints;
